import React, { useState, useRef, useCallback } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Loader2, UploadCloud, Image as ImageIcon, Check, HardDrive, Calendar, ChevronDown, X } from 'lucide-react'
import { useMedia, type MediaFile } from '@/services/use-media'
import { FilePreview, FilePreviewSidebar } from '@/components/media/file-preview'

const PICKER_PAGE_SIZE = 20

interface MediaPickerProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSelect: (url: string) => void
    title?: string
}

function PickerSkeleton() {
    return (
        <div className="aspect-square rounded-lg overflow-hidden">
            <Skeleton className="w-full h-full rounded-none" />
        </div>
    )
}

export function MediaPicker({ open, onOpenChange, onSelect, title = "Medya Seçimi" }: MediaPickerProps) {
    const { media, isLoading, uploadMedia, isUploading } = useMedia()
    const [activeTab, setActiveTab] = useState('library')
    const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null)
    const [visibleCount, setVisibleCount] = useState(PICKER_PAGE_SIZE)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const visibleMedia = media.slice(0, visibleCount)
    const hasMore = visibleCount < media.length

    const handleLoadMore = useCallback(() => {
        setIsLoadingMore(true)
        setTimeout(() => {
            setVisibleCount(prev => prev + PICKER_PAGE_SIZE)
            setIsLoadingMore(false)
        }, 400)
    }, [])

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        try {
            const res = await uploadMedia(file)
            if (res && res.media) {
                onSelect(res.media.path)
                onOpenChange(false)
                setActiveTab('library')
                setSelectedFile(null)
            }
        } catch (err) {
            console.error(err)
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = ''
        }
    }

    const handleConfirmSelection = () => {
        if (selectedFile) {
            onSelect(selectedFile.path)
            onOpenChange(false)
            setSelectedFile(null)
        }
    }

    const formatSize = (bytes: number) => {
        if (bytes === 0) return '0 B'
        const k = 1024
        const sizes = ['B', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] sm:max-h-[85vh] flex flex-col p-0 overflow-hidden">
                <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-0">
                    <DialogTitle className="text-base sm:text-lg">{title}</DialogTitle>
                    <DialogDescription className="text-xs sm:text-sm">
                        Kütüphaneden medya seçin veya yeni bir dosya yükleyin.
                    </DialogDescription>
                </DialogHeader>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
                    <div className="px-4 sm:px-6">
                        <TabsList className="w-full justify-start rounded-none bg-transparent border-b h-auto p-0 gap-2 sm:gap-4">
                            <TabsTrigger
                                value="library"
                                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=active]:bg-transparent px-1 pb-2.5 sm:pb-3 pt-2 text-xs sm:text-sm"
                            >
                                <ImageIcon className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" /> Kütüphane
                                {!isLoading && <Badge variant="secondary" className="ml-1.5 sm:ml-2 text-[9px] sm:text-[10px] h-4 sm:h-5">{media.length}</Badge>}
                            </TabsTrigger>
                            <TabsTrigger
                                value="upload"
                                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=active]:bg-transparent px-1 pb-2.5 sm:pb-3 pt-2 text-xs sm:text-sm"
                            >
                                <UploadCloud className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" /> Yükle
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    {/* LIBRARY TAB */}
                    <TabsContent value="library" className="flex-1 overflow-hidden p-0 m-0">
                        <div className="flex flex-col md:flex-row h-[60vh] sm:h-[520px]">
                            {/* Grid Area */}
                            <div className="flex-1 p-3 sm:p-4 overflow-y-auto">
                                {isLoading ? (
                                    <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
                                        {Array.from({ length: PICKER_PAGE_SIZE }).map((_, i) => (
                                            <PickerSkeleton key={i} />
                                        ))}
                                    </div>
                                ) : media.length === 0 ? (
                                    <div className="flex flex-col h-full items-center justify-center text-muted-foreground">
                                        <ImageIcon className="h-12 sm:h-16 w-12 sm:w-16 mb-4 opacity-15" />
                                        <p className="text-xs sm:text-sm">Henüz yüklenmiş medya bulunmuyor.</p>
                                        <Button
                                            variant="link"
                                            className="mt-2 text-xs sm:text-sm"
                                            onClick={() => setActiveTab('upload')}
                                        >
                                            İlk görseli yükleyin →
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-3 sm:space-y-4">
                                        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
                                            {visibleMedia.map((file) => {
                                                const isSelected = selectedFile?.path === file.path
                                                return (
                                                    <div
                                                        key={file.path}
                                                        className={`relative group cursor-pointer aspect-square rounded-lg overflow-hidden border-2 transition-all hover:shadow-md ${isSelected
                                                            ? 'border-primary ring-2 ring-primary/30 shadow-md'
                                                            : 'border-transparent hover:border-muted-foreground/30'
                                                            }`}
                                                        onClick={() => setSelectedFile(file)}
                                                    >
                                                        <FilePreview
                                                            filename={file.filename}
                                                            url={file.url}
                                                            mimetype={file.mimetype}
                                                        />
                                                        {isSelected && (
                                                            <div className="absolute inset-0 bg-primary/10">
                                                                <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 bg-primary text-primary-foreground rounded-full p-0.5 sm:p-1 shadow-lg">
                                                                    <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="absolute bottom-0 left-0 right-0 px-1.5 py-1 sm:px-2 sm:py-1.5 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <p className="text-[9px] sm:text-[10px] text-white truncate">{file.originalName}</p>
                                                        </div>
                                                    </div>
                                                )
                                            })}

                                            {isLoadingMore && Array.from({ length: 10 }).map((_, i) => (
                                                <PickerSkeleton key={`skeleton-${i}`} />
                                            ))}
                                        </div>

                                        {hasMore && !isLoadingMore && (
                                            <div className="flex justify-center py-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={handleLoadMore}
                                                    className="gap-1.5 text-xs text-muted-foreground"
                                                >
                                                    <ChevronDown className="h-3.5 w-3.5" />
                                                    Daha fazla göster ({media.length - visibleCount})
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Desktop Sidebar Detail Panel */}
                            <div className="hidden md:flex w-64 border-l bg-muted/20 flex-col">
                                {selectedFile ? (
                                    <div className="flex flex-col h-full">
                                        <div className="p-3 border-b">
                                            <div className="aspect-video bg-muted rounded-md overflow-hidden border">
                                                <FilePreviewSidebar
                                                    filename={selectedFile.filename}
                                                    url={selectedFile.url}
                                                    mimetype={selectedFile.mimetype}
                                                />
                                            </div>
                                        </div>
                                        <div className="p-3 flex-1 overflow-y-auto space-y-3">
                                            <div>
                                                <p className="text-xs font-semibold truncate" title={selectedFile.originalName}>
                                                    {selectedFile.originalName}
                                                </p>
                                                <Badge variant="outline" className="mt-1.5 text-[9px] font-normal">
                                                    {selectedFile.mimetype}
                                                </Badge>
                                            </div>
                                            <div className="space-y-2 text-[11px] text-muted-foreground">
                                                <div className="flex items-center gap-1.5">
                                                    <HardDrive className="h-3 w-3 shrink-0" />
                                                    <span>{formatSize(selectedFile.size)}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <Calendar className="h-3 w-3 shrink-0" />
                                                    <span>{new Date(selectedFile.createdAt).toLocaleDateString('tr-TR')}</span>
                                                </div>
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-muted-foreground mb-1">Dosya Yolu</p>
                                                <Input
                                                    readOnly
                                                    value={selectedFile.url}
                                                    className="h-7 text-[10px] font-mono bg-muted/50"
                                                    onClick={(e) => (e.target as HTMLInputElement).select()}
                                                />
                                            </div>
                                        </div>
                                        <div className="p-3 border-t">
                                            <Button className="w-full h-9" onClick={handleConfirmSelection}>
                                                <Check className="mr-2 h-4 w-4" />
                                                Bu Görseli Seç
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex-1 flex flex-col items-center justify-center text-center p-6 gap-3">
                                        <div className="rounded-full bg-muted p-4">
                                            <ImageIcon className="h-6 w-6 text-muted-foreground/40" />
                                        </div>
                                        <p className="text-xs text-muted-foreground leading-relaxed">
                                            Detayları görmek için<br />bir görsel seçin
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Mobile Bottom Bar - shows when file selected */}
                            {selectedFile && (
                                <div className="md:hidden border-t bg-background p-3 flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-md overflow-hidden border bg-muted shrink-0">
                                        <FilePreview
                                            filename={selectedFile.filename}
                                            url={selectedFile.url}
                                            mimetype={selectedFile.mimetype}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium truncate">{selectedFile.originalName}</p>
                                        <p className="text-[10px] text-muted-foreground">{formatSize(selectedFile.size)}</p>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0 shrink-0"
                                        onClick={() => setSelectedFile(null)}
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                    <Button size="sm" className="shrink-0 h-8" onClick={handleConfirmSelection}>
                                        <Check className="mr-1.5 h-3.5 w-3.5" />
                                        Seç
                                    </Button>
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {/* UPLOAD TAB */}
                    <TabsContent value="upload" className="flex-1 p-4 sm:p-6 m-0 h-[60vh] sm:h-[520px] flex items-center justify-center">
                        <div className="max-w-sm w-full border-2 border-dashed rounded-2xl p-6 sm:p-10 text-center hover:bg-muted/30 hover:border-primary/30 transition-all cursor-pointer"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <div className="rounded-full bg-primary/10 p-3 sm:p-4 w-fit mx-auto mb-4 sm:mb-5">
                                <UploadCloud className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
                            </div>
                            <h3 className="font-semibold text-sm sm:text-base mb-1.5">Dosya Yükle</h3>
                            <p className="text-[11px] sm:text-xs text-muted-foreground mb-4 sm:mb-6 leading-relaxed">
                                Görsel veya Video formatında<br />Max. 200MB boyutunda
                            </p>

                            <input
                                id="media-upload-input"
                                type="file"
                                className="hidden"
                                ref={fileInputRef}
                                accept="image/*,video/*"
                                onChange={handleFileSelect}
                                disabled={isUploading}
                            />
                            <Button
                                onClick={(e) => {
                                    e.stopPropagation()
                                    fileInputRef.current?.click()
                                }}
                                disabled={isUploading}
                                variant="outline"
                                className="w-full"
                            >
                                {isUploading ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Yükleniyor...</>
                                ) : (
                                    'Dosya Seç'
                                )}
                            </Button>
                        </div>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    )
}
